import { Link, SubtleMonochromeLink } from '@liveauctioneers/hammer-ui-core/link';
import { BodyPrimary, Caption, H1, H2, H3, H4, H5, H6 } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

type Props = {
    className?: string;
    content: string;
};

type MarkdownNode = { children?: MarkdownNode[]; tagName?: string; type: string };

const getExternalLinkProps = (href?: string) => ({
    rel: href?.startsWith('http') ? 'noopener noreferrer' : undefined,
    target: href?.startsWith('http') ? '_blank' : undefined,
});

const isMarkdownElement = (node: MarkdownNode | undefined, tagName: string) =>
    node?.type === 'element' && node.tagName === tagName;

const isLinkedImage = (node: MarkdownNode | undefined) =>
    isMarkdownElement(node, 'a') && node?.children?.length === 1 && isMarkdownElement(node.children?.[0], 'img');

// Prevents adding redundant paragraph styling/margins when an image is
// rendered on its own line, including `[![alt](src)](href)` linked images.
const isImageOnlyParagraph = (node: MarkdownNode | undefined) => {
    const onlyChild = node?.children?.length === 1 ? node.children[0] : undefined;
    return isMarkdownElement(onlyChild, 'img') || isLinkedImage(onlyChild);
};

const markdownComponents: Components = {
    a: ({ children, href, node }) => {
        if (isLinkedImage(node)) {
            return (
                <SubtleMonochromeLink
                    className="group block smMin:mx-auto smMin:w-fit"
                    href={href}
                    {...getExternalLinkProps(href)}
                >
                    {children}
                </SubtleMonochromeLink>
            );
        }

        return (
            <Link
                href={href}
                {...getExternalLinkProps(href)}
            >
                {children}
            </Link>
        );
    },
    blockquote: ({ children }) => (
        <blockquote className="my-sm border-l-2 border-border-secondary pl-sm text-text-secondary">
            {children}
        </blockquote>
    ),
    h1: ({ children }) => <H1 className="mb-md pt-xs">{children}</H1>,
    h2: ({ children }) => <H2 className="mb-md pt-xs">{children}</H2>,
    h3: ({ children }) => <H3 className="mt-md mb-xs">{children}</H3>,
    h4: ({ children }) => <H4 className="mt-md mb-xs">{children}</H4>,
    h5: ({ children }) => <H5 className="mt-sm mb-2xs">{children}</H5>,
    h6: ({ children }) => <H6 className="mt-sm mb-2xs">{children}</H6>,
    hr: () => <hr className="my-lg border-border-secondary" />,
    img: ({ alt, src, title }) => (
        <figure className="mb-lg flex w-full flex-col items-start gap-sm pt-sm smMin:mx-auto smMin:w-fit smMin:max-w-full smMin:items-center smMin:py-sm">
            <img
                alt={alt || ''}
                className="block h-auto w-full max-w-full object-contain smMin:w-auto"
                src={src}
            />
            {title && (
                <figcaption className="w-full text-left leading-[18px] smMin:text-center">
                    <Caption
                        className="group-hover:text-link-hover"
                        color="secondary"
                    >
                        {title}
                    </Caption>
                </figcaption>
            )}
        </figure>
    ),
    li: ({ children }) => <li className="mb-2xs">{children}</li>,
    ol: ({ children }) => <ol className="mb-sm ml-md list-decimal">{children}</ol>,
    p: ({ children, node }) => {
        if (isImageOnlyParagraph(node)) {
            return <>{children}</>;
        }

        // BodyPrimary is a span — keep block layout so paragraph spacing is preserved.
        return <BodyPrimary className="mb-sm block last:mb-0">{children}</BodyPrimary>;
    },
    pre: ({ children }) => (
        <pre className="font-mono mb-sm overflow-x-auto rounded-sm bg-background-secondary p-sm">{children}</pre>
    ),
    strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
    table: ({ children }) => (
        <div className="mb-sm w-full overflow-x-auto">
            <table className="w-full border-collapse">{children}</table>
        </div>
    ),
    td: ({ children }) => <td className="border-b border-border-secondary p-xs align-top">{children}</td>,
    th: ({ children }) => (
        <th className="border-b border-border-secondary p-xs text-left align-top font-bold">{children}</th>
    ),
    tr: ({ children }) => <tr>{children}</tr>,
    ul: ({ children }) => <ul className="mb-sm ml-md list-disc">{children}</ul>,
};

export const MarkdownContent = ({ className, content }: Props) => {
    return (
        <div className={cn('text-text-primary', className)}>
            <ReactMarkdown
                components={markdownComponents}
                remarkPlugins={[remarkGfm]}
            >
                {content}
            </ReactMarkdown>
        </div>
    );
};
